<template>
    <div class="available-games">
      <h2>Available Games to Join</h2>
      
      <div v-if="loading" class="loading">Loading games...</div>
      
      <div v-else>
        <div v-if="error" class="error">{{ error }}</div>
        
        <div v-if="games.length === 0" class="no-games">
          No available games found. Please create a new game.
        </div>
        
        <ul v-else class="game-list">
          <li v-for="game in games" :key="game.gameId" class="game-item">
            <span class="game-info">
              Game by {{ game.player1Name }} (ID: {{ game.gameId }})
            </span>
            <button 
              @click="joinGame(game.gameId)" 
              class="join-button"
            >
              Join Game
            </button>
          </li>
        </ul>
        
        <button @click="close" class="close-button">Close</button>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted } from 'vue'
  import { useGameStore } from '@/stores/game'
  import { useRouter } from 'vue-router'
  import api from '@/services/api'
  
  export default {
    emits: ['close'],
    
    setup(props, { emit }) {
      const gameStore = useGameStore()
      const router = useRouter()
      const games = ref([])
      const loading = ref(false)
      const error = ref(null)
  
      const fetchAvailableGames = async () => {
        loading.value = true
        error.value = null
        try {
          const response = await api.getAvailableGames()
          games.value = response.data
        } catch (err) {
          error.value = 'Failed to load available games. Please try again.'
          console.error('Error fetching games:', err)
        } finally {
          loading.value = false
        }
      }
  
      const joinGame = async (gameId) => {
        const playerName = prompt('Please enter your name to join the game:')
        if (!playerName) return
        
        try {
          loading.value = true
          await gameStore.joinGame(gameId, playerName)
          router.push({ name: 'game', params: { id: gameId } })
        } catch (err) {
          error.value = 'Failed to join the game. ' + 
            (err.response?.data?.message || 'Please try again.')
        } finally {
          loading.value = false
        }
      }
  
      const close = () => {
        emit('close')
      }
  
      onMounted(fetchAvailableGames)
  
      return {
        games,
        loading,
        error,
        joinGame,
        close
      }
    }
  }
  </script>
  
  <style scoped>
  .available-games {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  }
  
  h2 {
    margin-top: 0;
    color: #2c3e50;
    text-align: center;
  }
  
  .loading, .error, .no-games {
    text-align: center;
    padding: 20px;
    margin: 10px 0;
  }
  
  .loading {
    color: #007bff;
  }
  
  .error {
    color: #dc3545;
    background-color: #f8d7da;
    border-radius: 4px;
  }
  
  .no-games {
    color: #6c757d;
  }
  
  .game-list {
    list-style: none;
    padding: 0;
    margin: 20px 0;
  }
  
  .game-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 15px;
    margin-bottom: 10px;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .game-info {
    flex-grow: 1;
  }
  
  .join-button {
    padding: 5px 15px;
    background-color: #28a745;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .join-button:hover {
    background-color: #218838;
  }
  
  .close-button {
    display: block;
    width: 100%;
    padding: 8px;
    margin-top: 20px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
  }
  
  .close-button:hover {
    background-color: #5a6268;
  }
  </style>